<template>
  <div class="loading_container" v-if="loading">
    <div class="spinner-border text-primary" role="status">
      <span class="sr-only"></span>
    </div>
  </div>
  <div class="main" v-else>
    <!-- Side Navbar -->
    <SideBar />
    <!-- Body-Content -->
    <div class="body-content">
      <!-- Top Navbar -->
      <TopBar />
      <!-- Body -->
      <div class="container-fluid body-container">
        <div class="alert alert-danger" v-if="video.status === 3">
          Este vídeo está inativo.
        </div>
        <section class="video_play_page">
          <h1 class="video-title">
            {{ video.title }}
            <button class="btn btn-small" @click="editVideo()">Editar</button> &nbsp;
            <button class="btn btn-small btn-cancel" @click="deleteVideo(video)">
              Deletar
            </button>
          </h1>
          <div class="row">
            <div class="col-lg-12">
              <div class="video-container" v-if="video.type === 2 || video.type === 4">
                <video-js
                  id="player"
                  controls
                  controlsList="nodownload"
                  fluid="true"
                  class="video-js vjs-theme-city vjs-big-play-centered"
                >
                  <source
                    :src="getVideoURL(video)"
                    type="application/x-mpegURL"
                    data-setup='{ "playbackRates": [0.5, 1, 1.5, 2] }'
                  />
                </video-js>
              </div>
              <div
                class="iframe-embed-wrapper iframe-embed-responsive-16by9"
                v-else-if="video.type === 1"
              >
                <iframe
                  class="iframe-embed"
                  width="100%"
                  height="auto"
                  title="YouTube video player"
                  frameborder="0"
                  loading="lazy"
                  :src="video.path"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
                  allowfullscreen
                ></iframe>
              </div>
              <div v-else-if="video.type === 3" class="mb-3">
                <strong>Link para o meet: </strong>
                <a :href="video.config.meetup_link">{{ video.config.meetup_link }}</a>
              </div>
            </div>
            <div
              v-if="video.type !== 3"
              class="d-flex mb-2 mt-4"
              style="font-size: 14.2px; gap: 20px; flex-wrap: wrap; align-items: center"
            >
              <span class="fw-bold">{{
                formatPlural(video.views, "visualização", "visualizações")
              }}</span>

              <span class="fw-bold">{{
                formatPlural(video.likes, "likes", "likes")
              }}</span>
            </div>
            <div class="col-lg-12">
              <div class="video-details">
                <div class="wrap-box2 short mt-2">
                  <p class="break-word" style="font-size: 14.2px">
                    {{ video.description }}
                  </p>
                </div>

                <div v-if="video.config && video.config.links">
                  <h2 class="mt-3 mb-3">Links:</h2>
                  <ul>
                    <li v-for="link in video.config.links">
                      <a :href="link.src" class="link" target="_blank">{{
                        link.title
                      }}</a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="col-lg-12 mt-2" v-if="video.type !== 3">
              <hr style="margin-bottom: 5px" v-if="comments.length > 0" />
              <div class="alert alert-warning mt-3" v-if="comments.length === 0">
                Nenhum comentário sobre esse vídeo ainda.
              </div>
              <div class="comments" v-else>
                <div
                  class="box"
                  v-for="comment in comments"
                  :key="comment.id"
                  :id="comment.id"
                >
                  <img :src="getImageURL(comment.picture)" v-if="comment.picture" />
                  <img class="empty-image" v-else />
                  <div class="c_text_area">
                    <p class="name">
                      {{ comment.name ? comment.name : "Virtap"
                      }}<span>{{
                        formatElapsedTime(comment.registration_timestamp)
                      }}</span>
                    </p>
                    <div class="wrap-box2 short">
                      <p
                        class="break-word dsc"
                        v-html="formatMultilineText(comment.comment)"
                      ></p>
                    </div>
                    <div v-if="comment.status === 1">
                      <p class="under-review">
                        Este comentário está aguardando moderação e encontra-se invisível
                        no momento.
                      </p>
                      <div class="mt-2">
                        <button
                          class="btn btn-small bg-success"
                          style="margin-right: 5px"
                          @click="approveComment(comment)"
                        >
                          Aprovar
                        </button>
                        <button
                          class="btn btn-small bg-danger"
                          style="margin-right: 5px"
                          @click="reproveComment(comment)"
                        >
                          Reprovar
                        </button>
                        <button class="btn btn-small" @click="editComment(comment)">
                          Editar
                        </button>
                      </div>
                    </div>
                    <div v-if="comment.status === 2">
                      <div class="mt-2">
                        <button
                          class="btn btn-small bg-danger"
                          style="margin-right: 5px"
                          @click="deactivateComment(comment)"
                        >
                          Desativar
                        </button>
                        <button
                          class="btn btn-small"
                          @click="editComment(comment)"
                          style="margin-right: 5px"
                        >
                          Editar
                        </button>
                        <button
                          class="btn btn-small"
                          :class="{
                            'bg-success': comment.children_count === 0,
                            'bg-warning': comment.children_count > 0,
                          }"
                          @click="editReply(comment)"
                        >
                          {{
                            comment.children_count === 0
                              ? "Adicionar resposta"
                              : "Editar resposta"
                          }}
                        </button>
                      </div>
                    </div>
                    <div v-else-if="comment.status === 3">
                      <p class="inactive">Este comentário foi desativado.</p>
                      <div class="mt-2">
                        <button
                          class="btn btn-small bg-success"
                          style="margin-right: 5px"
                          @click="activateComment(comment)"
                        >
                          Ativar
                        </button>
                        <button class="btn btn-small" @click="editComment(comment)">
                          Editar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="page_number">
            <button
              id="fetchMoreComments"
              class="btn see_more btn-small"
              :class="{ disabled: loadingMore }"
              @click="fetchMore()"
              v-if="hasMore"
            >
              {{ loadingMore ? "Carregando..." : "Mostrar mais" }}
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import SideBar from "../SideBar.vue";
import TopBar from "../TopBar.vue";
import AdminManager from "../../../js/services/admin.js";
import StringUtils from "../../../js/utils/string.js";
import Config from "../../../../resources/config.js";

const RESULT_COUNT = 10;

export default {
  components: { SideBar, TopBar },

  created() {
    this.$store.commit("setActiveSection", "VideoPage");
    this.loadVideo();
  },

  data() {
    return {
      video: null,
      posting: false,
      comments: [],
      loadingMore: false,
      hasMore: true,
    };
  },

  computed: {
    loading() {
      return this.$store.state.loading;
    },
  },

  methods: {
    async loadVideo() {
      this.$store.commit("setLoading", true);
      try {
        let response = await AdminManager.getVideo(
          this.$route.params.videoId,
          RESULT_COUNT
        );
        this.video = response.video;
        this.comments = response.comments;
        this.hasMore = this.comments.length >= RESULT_COUNT;

        this.$store.commit("setActiveUser", response.profile);
        this.$store.commit("setLoading", false);
        this.applyTruncation();
        if (this.video.type === 2 || this.video.type === 4) {
          this.$nextTick(() => {
            let player = videojs("player", {
              playbackRates: [0.5, 1, 1.5, 2],
              html5: {
                hls: {
                  withCredentials: true,
                },
              },
            });
            player.play();
          });
        }
      } catch (error) {
        this.handleAPIError(error, this.$route.fullPath.replace(/^\/+/, ""));
      }
    },

    async approveComment(comment) {
      let action = async function () {
        let commentDetails = {
          status: 2,
        };

        let updatedComment = await AdminManager.updateVideoComment(
          comment.id,
          commentDetails
        );

        comment.status = updatedComment.status;
        return true;
      };

      this.showConfirmation(
        "Aprovar comentário",
        "Tem certeza que deseja aprovar este comentário?",
        "Aprovar",
        action,
        "Comentário aprovado",
        "Ocorreu um erro ao aprovar o comentário.\nPor favor, tente novamente."
      );
    },

    async activateComment(comment) {
      let action = async function () {
        let commentDetails = {
          status: 2,
        };

        let updatedComment = await AdminManager.updateVideoComment(
          comment.id,
          commentDetails
        );

        comment.status = updatedComment.status;
        return true;
      };

      this.showConfirmation(
        "Ativar comentário",
        "Tem certeza que deseja ativar este comentário?",
        "Ativar",
        action,
        "Comentário ativado",
        "Ocorreu um erro ao ativar o comentário.\nPor favor, tente novamente."
      );
    },

    async reproveComment(comment) {
      let action = async function () {
        let commentDetails = {
          status: 3,
        };

        let updatedComment = await AdminManager.updateVideoComment(
          comment.id,
          commentDetails
        );

        comment.status = updatedComment.status;
        return true;
      };

      this.showConfirmation(
        "Reprovar comentário",
        "Tem certeza que deseja reprovar este comentário?",
        "Reprovar",
        action,
        "Comentário reprovado",
        "Ocorreu um erro ao reprovar o comentário.\nPor favor, tente novamente."
      );
    },

    async deactivateComment(comment) {
      let action = async function () {
        let commentDetails = {
          status: 3,
        };

        let updatedComment = await AdminManager.updateVideoComment(
          comment.id,
          commentDetails
        );

        comment.status = updatedComment.status;
        return true;
      };

      this.showConfirmation(
        "Desativar comentário",
        "Tem certeza que deseja desativar este comentário?",
        "Desativar",
        action,
        "Comentário desativado",
        "Ocorreu um erro ao desativar o comentário.\nPor favor, tente novamente."
      );
    },

    async editVideo() {
      let $ctx = this;
      let $modal = $("#editVideoModal");
      $modal.find(".modal-title").text("Editar aula/mentoria");
      $modal.find(".item").removeClass("error");

      $modal
        .find("[data-video-type]")
        .off()
        .on("change", function () {
          if ($(this).val() === "3") {
            $modal.find("[data-meet-link-container]").show();
            $modal.find("[data-meet-release-container]").show();
            $modal.find("[data-video-release-container]").hide();
          } else {
            $modal.find("[data-meet-link-container]").hide();
            $modal.find("[data-meet-release-container]").hide();
            $modal.find("[data-video-release-container]").show();
          }

          if ($(this).val() === "2" || $(this).val() === "4") {
            $modal.find("[data-video-file-container]").show();
          } else {
            $modal.find("[data-video-file-container]").hide();
          }

          if ($(this).val() === "3" || $(this).val() === "4") {
            $modal.find("[data-video-course-container]").show();
          } else {
            $modal.find("[data-video-course-container]").hide();
          }

        });

      let action = async function () {
        let linksStr = $modal.find("[data-video-links]").val().split("\n");
        let links = [];

        let videoDetails = {
          type: $modal.find("[data-video-type]").val(),
          title: $modal.find("[data-video-title]").val(),
          description: $modal.find("[data-video-description]").val(),
          course_id: $modal.find("[data-video-course-id]").val(),
          path: $modal.find("[data-video-path]").val(),
          video_file: $modal.find("[data-video-file]").val(),
          links: links,
          release_threshold: null,
          status: $modal.find("[data-video-status]").val(),
        };

        let hasError = false;
        $modal.find(".item").removeClass("error");

        if (videoDetails.title.length < 10) {
          $modal.find("[data-video-title]").closest(".item").addClass("error");
          hasError = true;
        }
        if (videoDetails.path.length < 10) {
          $modal.find("[data-video-path]").closest(".item").addClass("error");
          hasError = true;
        }

        if (videoDetails.type === "2" || videoDetails.type === "4") {
          if (videoDetails.video_file.length < 10) {
            $modal.find("[data-video-file]").closest(".item").addClass("error");
            hasError = true;
          }
        }

        if (videoDetails.description.length < 50) {
          $modal.find("[data-video-description]").closest(".item").addClass("error");
          hasError = true;
        }

        if (linksStr && linksStr.length > 0) {
          let linkError = false;
          linksStr.forEach((l) => {
            if (!l || l.trim().length === 0) {
              return;
            }
            let link = l.split("|");
            if (!l || link.length !== 2) {
              linkError = true;
            } else {
              let url = link[0].trim();
              let title = link[1].trim();

              if (!url.startsWith("https://") || url.length < 20 || title.length < 3) {
                linkError = true;
              } else {
                links.push({
                  src: url,
                  title: title,
                });
              }
            }

            if (linkError) {
              $modal
                .find("[data-video-links]")
                .closest(".item")
                .addClass("error")
                .find("em")
                .text(
                  "O link >>>" +
                    l +
                    "<<< é inválido. Formato: https://link.com|titulo do link"
                );
              hasError = true;
            }
          });
        }

        if (videoDetails.type === "3") {
          const { DateTime } = luxon;
          const dt = DateTime.fromFormat(
            $modal.find("[data-live-release]").val(),
            "yyyy-MM-dd HH:mm:ss",
            {
              zone: "America/Sao_Paulo",
              setZone: true,
            }
          );

          if (!dt.isValid) {
            $modal.find("[data-live-release]").closest(".item").addClass("error");
            hasError = true;
          }
          videoDetails.release_threshold = dt.toJSDate().getTime();
          videoDetails.meetup_link = $modal.find("[data-meet-link]").val();

          if (
            !videoDetails.meetup_link.startsWith("https://") ||
            videoDetails.meetup_link.length < 20
          ) {
            $modal.find("[data-meet-link]").closest(".item").addClass("error");
            hasError = true;
          }
        } else {
          if ($modal.find("[data-video-release]").val()) {
            videoDetails.release_threshold =
              Number($modal.find("[data-video-release]").val()) * 24 * 3600 * 1000;
          }
        }

        if (hasError) {
          return false;
        }

        let updatedVideo = await AdminManager.updateVideo($ctx.video.id, videoDetails);

        $ctx.video = updatedVideo;
        return true;
      };

      let releaseDays = "";
      let releaseTimestamp = "";
      let meetup_link = "";
      let links = [];
      let videoFile = "";

      if (this.video.config) {
        if (
          this.video.config.release_strategy &&
          this.video.config.release_strategy.type === "RELEASE_DATE"
        ) {
          releaseDays =
            Number(this.video.config.release_strategy.timestamp) / 24 / 3600 / 1000;
        } else if (
          this.video.config.release_strategy &&
          this.video.config.release_strategy.type === "EXACT_DATE"
        ) {
          const { DateTime } = luxon;

          releaseTimestamp = DateTime.fromMillis(
            Number(this.video.config.release_strategy.timestamp),
            {
              zone: "America/Sao_Paulo",
            }
          ).toFormat("yyyy-MM-dd HH:mm:ss");
        }
        if (this.video.config.links) {
          links = this.video.config.links;
        }
        if (this.video.config.meetup_link) {
          meetup_link = this.video.config.meetup_link;
        }
      }

      let linksStr = "";
      links.forEach((link) => {
        if (linksStr.trim().length > 0) {
          linksStr += "\n";
        }
        linksStr += `${link.src}|${link.title}`;
      });

      if (this.video.config && this.video.config.video_file) {
        videoFile = this.video.config.video_file;
      }

      $modal.find("[data-video-type]").val($ctx.video.type);
      $modal.find("[data-video-title]").val($ctx.video.title);
      $modal.find("[data-video-description]").val($ctx.video.description);
      $modal.find("[data-video-path]").val($ctx.video.path);
      $modal.find("[data-video-file]").val(videoFile);
      if ($ctx.video.type === 3) {
        $modal.find("[data-meet-link]").val(meetup_link);
        $modal.find("[data-live-release]").val(releaseTimestamp);
        $modal.find("[data-meet-link-container]").show();
        $modal.find("[data-meet-release-container]").show();
        $modal.find("[data-video-release-container]").hide();
      } else {
        $modal.find("[data-video-release]").val(releaseDays);
        $modal.find("[data-meet-link-container]").hide();
        $modal.find("[data-meet-release-container]").hide();
        $modal.find("[data-video-release-container]").show();
      }

      if ($ctx.video.type === 2 || $ctx.video.type === 4) {
        $modal.find("[data-video-file-container]").show();
      } else {
        $modal.find("[data-video-file-container]").hide();
      }

      if ($ctx.video.type === 3 || $ctx.video.type === 4) {
        $modal.find("[data-video-course-container]").show();
        $modal.find("[data-video-course-id]").val($ctx.video.course_id);
      } else {
        $modal.find("[data-video-course-container]").hide();
      }

      $modal.find("[data-video-links]").val(linksStr);
      $modal.find("[data-video-status]").val($ctx.video.status);

      this.showModal(
        $modal,
        function () {
          $modal.find("[data-video-title]").focus();
        },
        "Salvar",
        action,
        "Aula/mentoria atualizada!",
        "Ocorreu um erro editar a aula/mentoria.\nPor favor, tente novamente."
      );
    },

    async deleteVideo(video) {
      let action = async function () {
        await AdminManager.deleteVideo(video.id);
        setTimeout(() => {
          location.href = "/aulas-e-mentorias";
        }, 1000);
        return true;
      };

      this.showConfirmation(
        "Deletar aula/mentoria",
        "Tem certeza que deseja deletar esta aula/mentoria?",
        "Deletar",
        action,
        "Aula/mentoria deletada!",
        "Ocorreu um erro ao deletar esta aula/mentoria.\nNota: conteúdos com comentários não podem ser deletados.\nVerifique os dados e tente novamente."
      );
    },
    async editComment(comment) {
      let $ctx = this;
      let $modal = $("#editCommentModal");
      $modal.find(".item").removeClass("error");

      let action = async function () {
        let commentDetails = {
          comment: $modal.find("[data-comment]").val(),
        };

        let hasError = false;
        $modal.find(".item").removeClass("error");

        if (commentDetails.comment.length < 10) {
          $modal.find("[data-comment]").closest(".item").addClass("error");
          hasError = true;
        }

        if (hasError) {
          return false;
        }

        let updatedComment = await AdminManager.updateVideoComment(
          comment.id,
          commentDetails
        );

        comment.comment = updatedComment.comment;
        return true;
      };

      $modal.find("[data-comment]").val(comment.comment);

      this.showModal(
        $modal,
        function () {
          $modal.find("[data-comment]").focus();
        },
        "Salvar",
        action,
        "Comentário atualizado.",
        "Ocorreu um erro editar o comentário.\nPor favor, tente novamente."
      );
    },
    async editReply(comment) {
      let $ctx = this;
      let $modal = $("#editReplyModal");
      $modal.find(".item").removeClass("error");
      $modal.find("[data-loading]").show();
      $modal.find("[data-content]").hide();
      $modal.find(".btn-close").addClass("disabled");
      $modal.find("[data-loading-error]").html("").hide();

      let action = async function () {
        let commentDetails = {
          comment: $modal.find("[data-comment]").val(),
        };

        let hasError = false;
        $modal.find(".item").removeClass("error");

        if (commentDetails.comment.length < 10) {
          $modal.find("[data-comment]").closest(".item").addClass("error");
          hasError = true;
        }

        if (hasError) {
          return false;
        }

        await AdminManager.replyVideoComment(comment.id, commentDetails);

        comment.children_count = 1;
        return true;
      };

      this.showModal(
        $modal,
        async function () {
          try {
            let response = await AdminManager.getVideoChildrenComments(comment.id);
            let txt = "";
            if (response.comments.length > 0) {
              txt = response.comments[0].comment;
            }
            $modal.find("[data-comment]").val(txt);
            $modal.find("[data-content]").show();
            $ctx.$nextTick(() => {
              $modal.find("[data-comment]").focus();
            });
          } catch (err) {
            console.log(err);
            $modal
              .find("[data-loading-error]")
              .html("Erro ao carregar resposta. Por favor, tente novamente")
              .show();
          } finally {
            $modal.find("[data-loading]").hide();
            $modal.find(".btn-close").removeClass("disabled");
          }
        },
        "Salvar",
        action,
        "Resposta atualizada.",
        "Ocorreu um erro editar a resposta.\nPor favor, tente novamente."
      );
    },
    async fetchMore() {
      let ctx = this;
      ctx.loadingMore = true;
      let lastComment = null;
      if (this.comments && this.comments.length > 0) {
        lastComment = this.comments[this.comments.length - 1];
      }

      try {
        let response = await AdminManager.getVideoComments(
          ctx.video.id,
          lastComment,
          RESULT_COUNT
        );

        this.comments = this.comments.concat(response.comments);
        if (response.comments.length < RESULT_COUNT) {
          this.hasMore = false;
        }

        ctx.$nextTick(() => {
          if (response.comments.length > 0) {
            let card = $(".comments")
              .find("#" + response.comments[0].id)
              .get(0);
            let scrollDiv = card.offsetTop - 10;
            window.scrollTo({ top: scrollDiv, behavior: "smooth" });
          }
        });
      } catch (err) {
        console.log(err);
      } finally {
        ctx.loadingMore = false;
      }
    },
  },
};
</script>
